import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { activitiesOfSchedule } from '@d-orca/shared/client/activities';
import type { ISchedulerDependency, ISchedulerGroup } from '@interfaces/IScheduler';
import { fetchSchedules, fetchSchedule } from '@apis/schedule';
import { scheduleListKey, scheduleKey } from '@constants/queryKeys';
import { retryUnlessProviderError } from '@utils/queryRetry';
import { useActiveScheduleNumber } from '@hooks/useActiveSchedule';
import { useCommittedActivities } from './useCommittedActivities';

/**
 * The page's data root: COMMITTED activities (the feed) over the plan they were
 * committed from (the schedule).
 *
 * Both reads are needed and neither substitutes for the other:
 *   · the feed decides WHICH rows exist — an activity is a promise, so an
 *     uncommitted leaf, a parent and a milestone are simply not on this page
 *     (2026-09-17 task-commit design);
 *   · the schedule supplies the surrounding plan — groups, dependencies and the
 *     full task array the shared TaskPanel drawer is bootstrapped from.
 *
 * `tasks` is therefore the feed's tasks; `detail` stays the whole schedule.
 */
// Stable empties, so memos keyed on them hold until the schedule loads.
const NO_GROUPS: ISchedulerGroup[] = [];
const NO_DEPS: ISchedulerDependency[] = [];

/** `scheduleNumber`: read THIS schedule rather than the TopBar's pick — for a
 *  host whose item names its schedule (a productivity row can come from any). */
export function useActivitiesFromSchedule(siteId?: string, scheduleNumber?: string) {
  const list = useQuery({
    queryKey: scheduleListKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchSchedules(siteId!, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
    retry: retryUnlessProviderError,
  });
  // The schedule the user picked in the TopBar (shared across every surface);
  // falls back to the most-recent schedule when nothing is selected — unless
  // the caller named one.
  const activeScheduleNumber = useActiveScheduleNumber(siteId, list.data ?? []);
  const resolvedScheduleNumber = scheduleNumber ?? activeScheduleNumber;
  const detail = useQuery({
    queryKey: scheduleKey(siteId ?? '', resolvedScheduleNumber),
    queryFn: ({ signal }) => fetchSchedule(siteId!, resolvedScheduleNumber!, signal),
    enabled: !!siteId && !!resolvedScheduleNumber,
    staleTime: Infinity,
    retry: retryUnlessProviderError,
  });
  const committed = useCommittedActivities(siteId);
  // The site-scoped feed narrowed to the schedule on screen, plus Trusstor's
  // status per task — memoised as one, so both keep their identity until the
  // feed or the schedule changes.
  const { tasks, activityStatusById } = useMemo(
    () => activitiesOfSchedule(committed.activities, resolvedScheduleNumber),
    [committed.activities, resolvedScheduleNumber],
  );

  return {
    tasks,
    activityStatusById,
    groups: detail.data?.groups ?? NO_GROUPS,
    deps: detail.data?.dependencies ?? NO_DEPS,
    // The resolved schedule number — needed to deep-link into the Scheduler.
    scheduleNumber: resolvedScheduleNumber,
    // The whole site's feed, before the narrowing — for a link that names a
    // promise from another schedule.
    committed,
    weeklyDaysOff: detail.data?.weeklyDaysOff,
    // The full schedule document — lets a consumer bootstrap the scheduler store
    // (e.g. ActivitiesPage mounting the shared TaskPanel drawer).
    detail: detail.data,
    isLoading: list.isLoading || detail.isLoading || committed.isLoading,
    // Trusstor unreachable → the feed 502s and the page shows an error. There is
    // no cached status to fall back to, by design (invariant 7).
    isError: list.isError || detail.isError || committed.isError,
    // Whether the page has ever had data — lets it distinguish a first-load
    // failure (nothing to show) from a failed background refetch (data kept).
    hasData: detail.data != null && committed.hasData,
  };
}
